import user from './user';
import { appData } from './common';
import { get } from '@intigral/utils/lib/browser-storage/localStorage';
import { CACHE_CONFIG_TYPES } from '@intigral/constant';
import { getBaseSubscription } from '@intigral/utils/lib/user';

/**
 * Determines if a lock icon should be shown for PPV channels based on user's PPV subscription status.
 *
 * @param {any} channel - The channel object with isPPV and subscriptions properties.
 * @returns {boolean} - Returns true if the lock icon should be shown, false otherwise.
 */
export function checkShowLockIconForPPV(channel: any): boolean {
    const userInfo = user.getUserInfo();
    let ppvSubscribedList = userInfo?.ppvList || [];

    // Fallback: Try to get from localStorage cache if not in userInfo
    if (!ppvSubscribedList || ppvSubscribedList.length === 0) {
        const cachedUser = get(CACHE_CONFIG_TYPES.user);
        ppvSubscribedList = cachedUser?.ppvList || [];
    }

    if (!channel?.isPPV) {
        return true; // Not a PPV channel, use original logic
    }

    // For PPV channels, check if user is subscribed to any PPV event for this channel
    const channelSubscriptionGuids = channel?.subscriptions?.map((sub) => sub.subscriptionGUID) || [];

    const isSubscribedToPPV = channelSubscriptionGuids.some((guid) =>
        ppvSubscribedList.some((ppvEvent) => {
            // Check all possible property names in the PPV list
            const matchBySubscriptionGuid = ppvEvent.subscriptionGuid === guid;
            const matchByGuid = ppvEvent.guid === guid;
            const matchByEventId = ppvEvent.eventId === guid;

            return matchBySubscriptionGuid || matchByGuid || matchByEventId;
        })
    );

    // If user is subscribed to PPV, don't show lock (return false)
    // If user is not subscribed to PPV, show lock (return true)
    return !isSubscribedToPPV;
}

/**
 * Determines if a lock icon should be shown based on subscription status.
 * Preserves all original logic and adds PPV-specific handling.
 *
 * @param {string[]} subscriptionGuids - Array of subscription GUIDs for the content.
 * @param {any} channel - Optional channel object for PPV-specific logic.
 * @returns {boolean} - Returns true if the lock icon should be shown, false otherwise.
 */
export function checkShowLockIcon(subscriptionGuids: string[] = [], channel?: any, configParam?: any): boolean {
    // First, check PPV-specific logic if it's a PPV channel
    if (channel?.isPPV) {
        const ppvLockStatus = checkShowLockIconForPPV(channel);

        // For PPV channels, ALWAYS return the PPV-specific result
        return ppvLockStatus;
    }

    // ORIGINAL LOGIC - Keep all existing conditions intact
    // Use provided config or fallback to localStorage for backward compatibility
    const config = configParam;
    const userInfo = user.getUserInfo();
    // Guests / incomplete sessions have no subscriptionGUID array; spreading null/undefined throws.
    const userSubGuids = Array.isArray(userInfo?.subscriptionGUID) ? [...userInfo.subscriptionGUID] : [];

    // Original condition 1: If no subscription GUIDs provided, don't show lock
    if (!subscriptionGuids || subscriptionGuids.length === 0) {
        return false;
    }

    // Original condition 2: Check if user has any of the required subscriptions
    const hasSubscription = subscriptionGuids.some((guid) => userSubGuids.includes(guid));

    // Original condition 3: Show lock if user doesn't have the subscription
    return !hasSubscription;
}

import { showUpgradedModal } from '../store/reducers/generalConfigurationSlice';

/**
 * Subscription-tier lock (excludes item.isLocked PPV-style locks).
 * Matches MoviePlayButton behavior.
 */
export function isSubscriptionLockedContent(item: { isLocked?: boolean } | null | undefined, locked: boolean): boolean {
    return !item?.isLocked && locked;
}

export function showUpgradeOrOpenDetail({
    item,
    locked,
    dispatch,
    openDetail,
}: {
    item?: { isLocked?: boolean } | null;
    locked: boolean;
    dispatch: (action: ReturnType<typeof showUpgradedModal>) => void;
    openDetail: () => void;
}): void {
    // if (isSubscriptionLockedContent(item, locked)) {
    //     dispatch(showUpgradedModal(true));
    //     return;
    // }
    openDetail();
}

export function showUpgradeOrRun(
    isLocked: boolean,
    dispatch: (action: ReturnType<typeof showUpgradedModal>) => void,
    run: () => void
): void {
    if (isLocked) {
        dispatch(showUpgradedModal(true));
        return;
    }
    run();
}

export const getPaidSubscriptionGuids = (configParam?: any): string => {
    // Use provided config or fallback to localStorage for backward compatibility
    const config = configParam;
    const PaidBaseSubscription: string[] = config?.paidVsFree?.paidSubscriptionGuids || [];

    return encodeURIComponent(PaidBaseSubscription.join('|'));
};
